'use client'

import { subscribeNavigation } from '@go-beyond/react/browser'
import { OrigensLogo } from '@origens/ui'
import { ArrowLeft } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/utils.js'

export type SidebarItem = {
  label: string
  href: string
  icon: ComponentType<{ className?: string }>
  match?: 'exact' | 'prefix'
  sectionLabel?: string
  hardNavigation?: boolean
}

function currentLocation() {
  if (typeof window === 'undefined') return { pathname: '', search: '' }
  return { pathname: window.location.pathname, search: window.location.search }
}

function isItemActive(item: SidebarItem, pathname: string, search: string) {
  const target = new URL(item.href, 'http://local')
  if (target.search) {
    return pathname === target.pathname && search === target.search
  }
  if (item.match === 'prefix') {
    return pathname === target.pathname || pathname.startsWith(`${target.pathname}/`)
  }
  return pathname === target.pathname && search === ''
}

function SidebarNav({
  items,
  pathname,
  search,
}: {
  items: SidebarItem[]
  pathname: string
  search: string
}) {
  return (
    <nav aria-label="Section navigation" className="grid gap-1">
      {items.map((item) => {
        const active = isItemActive(item, pathname, search)
        const Icon = item.icon
        const link = (
          <a
            href={item.href}
            onClick={item.hardNavigation ? (event) => { event.preventDefault(); window.location.assign(item.href) } : undefined}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'group flex min-h-9 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium no-underline transition-colors',
              active
                ? 'bg-[var(--muted)] text-[var(--ink)]'
                : 'text-[var(--ink-muted)] hover:bg-[var(--muted)] hover:text-[var(--ink)]',
            )}
          >
            <Icon
              className={cn(
                'h-4 w-4 shrink-0',
                active ? 'text-[var(--accent)]' : 'text-[var(--ink-muted)]',
              )}
            />
            <span>{item.label}</span>
          </a>
        )

        return <div key={item.href}>
          {item.sectionLabel ? <p className="mb-1 mt-5 px-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{item.sectionLabel}</p> : null}
          {link}
        </div>
      })}
    </nav>
  )
}

function MobileNav({
  title,
  backHref,
  backLabel,
  items,
  pathname,
  search,
}: {
  title: string
  backHref?: string
  backLabel?: string
  items: SidebarItem[]
  pathname: string
  search: string
}) {
  const [brandProgress, setBrandProgress] = useState(0)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    const update = () => {
      frameRef.current = null
      const progress = Math.min(1, Math.max(0, (window.scrollY - 12) / 44))
      setBrandProgress(progress)
    }
    const scheduleUpdate = () => {
      if (frameRef.current !== null) return
      frameRef.current = window.requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', scheduleUpdate, { passive: true })
    return () => {
      window.removeEventListener('scroll', scheduleUpdate)
      if (frameRef.current !== null) window.cancelAnimationFrame(frameRef.current)
    }
  }, [])

  return (
    <div className="sticky top-0 z-40 border-b border-[var(--border)] bg-[var(--card)]/95 backdrop-blur md:hidden">
      <div className="flex min-h-12 items-center gap-2 px-3">
        {backHref || title ? (
          <div className="flex shrink-0 items-center border-r border-[var(--border)] pr-2">
            {backHref ? (
              <div className="relative h-8 w-8">
                <a
                  href={backHref}
                  aria-label={backLabel ?? 'Back'}
                  aria-hidden={brandProgress >= 0.5}
                  tabIndex={brandProgress < 0.5 ? 0 : -1}
                  title={backLabel ?? 'Back'}
                  className="absolute inset-0 inline-flex items-center justify-center rounded-md text-[var(--ink-muted)] no-underline will-change-[opacity,transform] hover:bg-[var(--muted)] hover:text-[var(--ink)]"
                  style={{
                    opacity: 1 - brandProgress,
                    pointerEvents: brandProgress < 0.5 ? 'auto' : 'none',
                    transform: `scale(${1 - brandProgress * 0.12})`,
                  }}
                >
                  <ArrowLeft className="h-4 w-4" />
                </a>
                <a
                  href="/"
                  aria-label="Origens home"
                  aria-hidden={brandProgress < 0.5}
                  tabIndex={brandProgress >= 0.5 ? 0 : -1}
                  className="absolute inset-0 inline-flex items-center justify-center no-underline will-change-[opacity,transform]"
                  style={{
                    opacity: brandProgress,
                    pointerEvents: brandProgress >= 0.5 ? 'auto' : 'none',
                    transform: `scale(${0.84 + brandProgress * 0.16})`,
                  }}
                >
                  <OrigensLogo className="h-7 w-7" />
                </a>
              </div>
            ) : (
              <>
                <a
                  href="/"
                  aria-label="Origens home"
                  aria-hidden={brandProgress < 0.9}
                  tabIndex={brandProgress >= 0.9 ? 0 : -1}
                  className="block overflow-hidden no-underline will-change-[width,opacity,transform]"
                  style={{
                    width: `${brandProgress * 28}px`,
                    marginRight: `${brandProgress * 6}px`,
                    opacity: brandProgress,
                    transform: `translateY(${(1 - brandProgress) * -8}px) scale(${
                      0.85 + brandProgress * 0.15
                    })`,
                  }}
                >
                  <OrigensLogo className="h-7 w-7 max-w-none" />
                </a>
                <span className="max-w-28 truncate text-sm font-semibold tracking-tight">
                  {title}
                </span>
              </>
            )}
          </div>
        ) : null}
        <nav
          aria-label="Section navigation"
          className="flex min-w-0 flex-1 snap-x items-center gap-1 overflow-x-auto py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((item) => {
            const active = isItemActive(item, pathname, search)
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={item.hardNavigation ? (event) => { event.preventDefault(); window.location.assign(item.href) } : undefined}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'shrink-0 snap-start rounded-md px-3 py-1.5 text-sm font-medium no-underline transition-colors',
                  active
                    ? 'bg-[var(--muted)] text-[var(--ink)]'
                    : 'text-[var(--ink-muted)] hover:bg-[var(--muted)] hover:text-[var(--ink)]',
                )}
              >
                {item.label}
              </a>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

export function SidebarLayout({
  title,
  description,
  titleIcon: TitleIcon,
  backHref,
  backLabel,
  items,
  footer,
  children,
}: {
  title: string
  description?: string
  titleIcon?: ComponentType<{ className?: string }>
  backHref?: string
  backLabel?: string
  items: SidebarItem[]
  footer?: ReactNode
  children: ReactNode
}) {
  const [location, setLocation] = useState(currentLocation)

  useEffect(() => {
    const update = () => setLocation(currentLocation())
    const unsubscribe = subscribeNavigation((event) => {
      if (event.type === 'success') update()
    })
    window.addEventListener('popstate', update)
    return () => {
      unsubscribe()
      window.removeEventListener('popstate', update)
    }
  }, [])

  const sidebar = (
    <>
      {backHref || title || description ? (
        <div className="mb-5 flex items-start gap-2">
          {backHref ? (
            <a
              href={backHref}
              aria-label={backLabel ?? 'Back'}
              title={backLabel ?? 'Back'}
              className="-ml-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--ink-muted)] no-underline transition hover:bg-[var(--muted)] hover:text-[var(--ink)]"
            >
              <ArrowLeft className="h-4 w-4" />
            </a>
          ) : null}
          <div className="min-w-0 pt-1">
            {title ? (
              <p className="flex items-center gap-2 truncate text-sm font-semibold tracking-tight text-[var(--ink)]">
                {TitleIcon ? <TitleIcon className="h-4 w-4 shrink-0 text-[var(--ink-muted)]" /> : null}
                <span className="truncate">{title}</span>
              </p>
            ) : null}
            {description ? (
              <p className="mt-1 text-xs leading-5 text-[var(--ink-muted)]">{description}</p>
            ) : null}
          </div>
        </div>
      ) : null}
      <SidebarNav items={items} pathname={location.pathname} search={location.search} />
      {footer ? <div className="mt-auto pt-6">{footer}</div> : null}
    </>
  )

  return (
    <div className="min-h-[calc(100dvh-65px)] md:grid md:grid-cols-[15rem_minmax(0,1fr)]">
      <aside className="hidden p-3 pr-0 md:block">
        <div className="sticky top-3 flex min-h-[calc(100dvh-5.6rem)] flex-col rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm">
          {sidebar}
        </div>
      </aside>

      <div className="min-w-0">
        <MobileNav
          title={title}
          {...(backHref ? { backHref } : {})}
          {...(backLabel ? { backLabel } : {})}
          items={items}
          pathname={location.pathname}
          search={location.search}
        />
        {children}
      </div>
    </div>
  )
}
